/**
 * SyncStatusPanel — observability for the offline outbox.
 *
 * Reads the outbox directly (getPending → pending + error items) and shows how
 * many reports are queued, which ones failed and WHY, plus a manual retry.
 * Hidden entirely when there is nothing queued (total === 0).
 */
import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Trash2, WifiOff } from "lucide-react";
import { Button } from "@/shared/ui/button.js";
import { cn } from "@/shared/lib/utils.js";
import {
  getPending,
  getTerminalFailed,
  summarizeOutbox,
  type OutboxSummary,
} from "@/features/offline-sync/index.js";

const labelClass =
  "font-mono text-[0.625rem] font-medium uppercase tracking-[0.18em] text-muted-foreground";

const EMPTY: OutboxSummary = { pendientes: 0, errores: [], terminales: [], total: 0 };

export function SyncStatusPanel(props: {
  online: boolean;
  sincronizando: boolean;
  onSincronizar: () => void;
  /** Discard terminal-failed reports (destructive). Shown only when there are any. */
  onLimpiar: () => void;
  /** Bumped by the parent after a sync/list refresh so the summary re-reads. */
  reloadKey: number;
}) {
  const { online, sincronizando, onSincronizar, onLimpiar, reloadKey } = props;
  const [resumen, setResumen] = useState<OutboxSummary>(EMPTY);

  useEffect(() => {
    let activo = true;
    // Fetch both auto-retryable pending/error items and terminal (failed) items.
    void Promise.all([getPending(), getTerminalFailed()]).then(([items, terminalItems]) => {
      if (activo) setResumen(summarizeOutbox(items, terminalItems));
    });
    return () => {
      activo = false;
    };
  }, [reloadKey, online, sincronizando]);

  // Nothing queued → no panel.
  if (resumen.total === 0) return null;

  const hayErrores = resumen.errores.length > 0;
  const hayTerminales = resumen.terminales.length > 0;

  return (
    <section
      className={cn(
        "rounded-lg border bg-card p-5 space-y-4",
        hayErrores || hayTerminales ? "border-destructive/40" : "border-border",
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <p className={labelClass}>Sincronización</p>
          <p className="text-sm text-foreground">
            {resumen.pendientes} pendiente{resumen.pendientes === 1 ? "" : "s"}
            {hayErrores && (
              <>
                {" · "}
                <span className="text-destructive">
                  {resumen.errores.length} con error
                </span>
              </>
            )}
            {hayTerminales && (
              <>
                {" · "}
                <span className="text-destructive font-medium">
                  {resumen.terminales.length} necesita{resumen.terminales.length === 1 ? "" : "n"} atención
                </span>
              </>
            )}
          </p>
        </div>
        {online ? (
          <Button
            variant="outline"
            size="sm"
            disabled={sincronizando}
            onClick={onSincronizar}
            className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.12em]"
          >
            {sincronizando ? (
              <Loader2 className="size-3.5 animate-spin" aria-hidden />
            ) : (
              <RefreshCw className="size-3.5" aria-hidden />
            )}
            Reintentar
          </Button>
        ) : (
          <span className={cn(labelClass, "inline-flex items-center gap-1.5 text-destructive")}>
            <WifiOff className="size-3.5" aria-hidden />
            Sin señal — se reintenta al reconectar
          </span>
        )}
      </div>

      {hayErrores && (
        <ul className="space-y-2 border-t border-border pt-3">
          {resumen.errores.map((e) => (
            <li
              key={e.localId}
              className="flex items-start gap-2 text-xs text-muted-foreground"
            >
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-destructive" aria-hidden />
              <span>
                <span className="font-medium text-foreground">
                  {e.tipo} · {e.cliente || "Sin cliente"}
                </span>
                {e.lastError ? ` — ${e.lastError}` : " — error desconocido"}
                {e.attempts > 0 && ` (intento ${e.attempts})`}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Fase 3: terminal items — exhausted MAX_ATTEMPTS, require manual retry. */}
      {hayTerminales && (
        <ul className="space-y-2 border-t border-border pt-3">
          {resumen.terminales.map((e) => (
            <li
              key={e.localId}
              className="flex items-start gap-2 text-xs text-muted-foreground"
            >
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-destructive" aria-hidden />
              <span>
                <span className="font-medium text-foreground">
                  {e.tipo} · {e.cliente || "Sin cliente"}
                </span>
                {" — "}
                No se pudo sincronizar tras varios intentos. Reintentar manualmente.
              </span>
            </li>
          ))}
          <li className="flex justify-end pt-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={onLimpiar}
              className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.12em] text-destructive hover:text-destructive"
            >
              <Trash2 className="size-3.5" aria-hidden />
              Limpiar
            </Button>
          </li>
        </ul>
      )}

      {!hayErrores && !hayTerminales && online && (
        <p className="flex items-center gap-1.5 border-t border-border pt-3 text-xs text-muted-foreground">
          <CheckCircle2 className="size-3.5 text-green-500" aria-hidden />
          Todo en cola se sincroniza automáticamente.
        </p>
      )}
    </section>
  );
}
